import { QualifierRepository } from '../../repositories/qualifier/QualifierRepository';
import { QualifierService } from '../../repositories/qualifier/QualifierService';
import { QualifierFactory } from '../../../plugins/engage/qualifiers/QualifierFactory';
import { View } from '../../modules/view/View';
import { logService, eventsService, AppEvents } from '@Engage.Core';
import { ActionRepository } from '../../../plugins/engage/actions/triggers/ActionRepository';
import { GlobalLocation } from '../../../application/GeoLocation';
class ViewsServiceClass {
    async processView() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        logService.log('[ViewsService] processView start');
        // Step 1+2 — view init and qualifier fetch in parallel.
        // If setViewData() fails we still have qualifiers but meterViews will be empty —
        // the paywall will receive zeroed counts which is the safe fallback.
        const [viewDataResult, qualifiers] = await Promise.allSettled([
            View.setViewData(),
            new QualifierService().getQualifiers()
        ]);
        if (viewDataResult.status === 'rejected') {
            logService.warn('[ViewsService] setViewData failed, proceeding with empty views', viewDataResult.reason);
        }
        if (qualifiers.status === 'rejected') {
            logService.warn('[ViewsService] getQualifiers failed', qualifiers.reason);
            return null;
        }
        const qualifierList = qualifiers.value;
        logService.log('[ViewsService] View data and qualifiers ready', `qualifierCount=${Object.keys(qualifierList).length}`);
        // Step 3 — POST /paywall
        let paywallResponse = null;
        try {
            const paywallPayload = await QualifierFactory.processAllAsync(qualifierList);
            // Add conversation context to payload (server needs these for expiration logic)
            const startDate = View.getConversationStartDate();
            paywallPayload.conversationStartDate = startDate ? startDate.toISOString() : null;
            paywallPayload.activeConversationId = View.getActiveConversationId();
            paywallPayload.activeConversationCode = View.getActiveConversationCode();
            paywallPayload.clientIp = GlobalLocation.getIP() || null;
            paywallPayload.clientUserAgent = navigator.userAgent || null;
            paywallPayload.clientDate = new Date().toISOString();
            logService.log('[ViewsService] Posting to paywall');
            paywallResponse = await QualifierRepository.getPaywall(paywallPayload);
        }
        catch (err) {
            logService.error('[ViewsService] Paywall POST failed', err);
            return null;
        }
        if (!paywallResponse) {
            logService.warn('[ViewsService] No paywall response received');
            return null;
        }
        // --- Allowed Access List Check (mirrors ConneXt v2 AccessManager) ---
        const accessGranted = paywallResponse.accessGranted;
        if (accessGranted) {
            const reason = paywallResponse.accessGrantedReason || 'IP Whitelist';
            logService.log('[ViewsService] Access granted — skipping paywall', `reason=${reason}`);
            eventsService.dispatch(AppEvents.AccessGranted, { Name: reason });
            // Don't track views or show actions for whitelisted users
            return paywallResponse;
        }
        const { conversationCode, conversationId, conversationType, meterLevel } = paywallResponse;
        const resolvedMeterLevel = meterLevel || conversationType || '1';
        const resolvedCode = conversationCode !== null && conversationCode !== void 0 ? conversationCode : 'null';
        const resolvedId = conversationId !== null && conversationId !== void 0 ? conversationId : -1;
        logService.log('[ViewsService] Paywall response', `resolvedMeterLevel=${resolvedMeterLevel}`, `conversationCode=${resolvedCode}`, `conversationId=${resolvedId}`, `showAction=${paywallResponse.showAction}`);
        const meterLevelId = this.getMeterLevelId(resolvedMeterLevel);
        // Step 4 — set active meter + increment
        eventsService.dispatch(AppEvents.MeterLevelSet, {
            MeterLevel: resolvedMeterLevel,
            MeterLevelId: meterLevelId,
            MeterLevelMethod: 'Default',
            Method: 'Default',
            Level: resolvedMeterLevel,
            Rule: null
        });
        View.setActiveMeterView(resolvedMeterLevel);
        // --- Conversation Expiration Handling (mirrors ConneXt v2 Campaign.getConversation + Conversation.expired) ---
        const expiration = paywallResponse.expiration || paywallResponse.Expiration;
        if (expiration && (expiration.isExpired || expiration.IsExpired)) {
            const reason = expiration.expiredTypeName || expiration.ExpiredTypeName;
            const resetViews = expiration.resetConversationViews || expiration.ResetConversationViews;
            logService.log('[ViewsService] Conversation expired', `reason=${reason}`, `resetViews=${resetViews}`, `recalcId=${expiration.recalculatedConversationId || expiration.RecalculatedConversationId}`);
            const prevCode = View.getActiveConversationCode();
            // Reset views based on ResetConversationViews (mirrors Conversation.resetViewExpirationConversation)
            if (resetViews === 'Current' && prevCode) {
                View.resetConversationView(prevCode);
            }
            else if (resetViews === 'All') {
                View.resetMeterLevel();
            }
            // Reset repeatable action data for the expired conversation
            ActionRepository.removeRepeatablesInConv();
            ActionRepository.removeRepeatablesInArticle();
            // Dispatch ConversationExpired event (mirrors ConneXt v2 eventsService.dispatch(AppEvents.ConversationExpired))
            eventsService.dispatch(AppEvents.ConversationExpired, {
                Conversation: {
                    Id: (_b = (_a = View.activeMeterView) === null || _a === void 0 ? void 0 : _a.activeConversationId) !== null && _b !== void 0 ? _b : null,
                    Code: prevCode !== null && prevCode !== void 0 ? prevCode : null
                },
                NextConversation: {
                    Id: resolvedId,
                    Code: resolvedCode
                },
                Reason: reason,
                ArticleCountReset: resetViews
            });
        }
        const prevConversationCode = View.getActiveConversationCode();
        View.incrementConversationCount(resolvedCode, resolvedId);
        if (prevConversationCode && prevConversationCode !== resolvedCode) {
            View.setRequestForSaveView(resolvedCode, prevConversationCode);
        }
        eventsService.dispatch(AppEvents.ConversationDetermined, {
            Conversation: {
                Id: resolvedId,
                Code: resolvedCode,
                Name: (_c = paywallResponse.conversationName) !== null && _c !== void 0 ? _c : null,
                ViewCount: View.getCurrentConversationViewCount(),
                StartDate: (_d = View.getConversationStartDate()) !== null && _d !== void 0 ? _d : null,
                Properties: {
                    Date: {
                        Started: (_f = (_e = View.getConversationStartDate()) === null || _e === void 0 ? void 0 : _e.toISOString()) !== null && _f !== void 0 ? _f : null,
                        Expiration: null
                    },
                    Views: View.getCurrentConversationViewCount(),
                    FreeViewsLeft: View.isFreeArticleAvailable() || 0,
                    Paywall: {
                        Limit: 0,
                        FreeViewsLeft: View.isFreeArticleAvailable() || 0
                    }
                }
            },
            // camelCase fields for direct GTM dataLayer push
            conversationName: (_g = paywallResponse.conversationName) !== null && _g !== void 0 ? _g : null,
            conversationViews: View.getCurrentConversationViewCount(),
            conversationDateStarted: (_j = (_h = View.getConversationStartDate()) === null || _h === void 0 ? void 0 : _h.toISOString()) !== null && _j !== void 0 ? _j : null,
            conversationDateExpiratation: null,
            conversationPaywallLimit: 0,
            conversationViewsLeft: View.isFreeArticleAvailable() || 0,
            CampaignId: (_k = paywallResponse.campaignId) !== null && _k !== void 0 ? _k : null,
            CampaignName: (_l = paywallResponse.campaignName) !== null && _l !== void 0 ? _l : null,
            MeterLevel: resolvedMeterLevel,
            MeterLevelId: meterLevelId,
            MeterLevelMethod: 'Default',
            Method: 'Default'
        });
        // Step 5 — persist cookie + conditionally POST views (fire-and-forget with error logging)
        try {
            const saveResult = View.saveViewInformation();
            if (saveResult instanceof Promise) {
                saveResult.catch(err => logService.error('[ViewsService] POST views failed', err));
            }
        }
        catch (err) {
            logService.error('[ViewsService] saveViewInformation failed', err);
        }
        logService.log('[ViewsService] processView complete');
        return paywallResponse;
    }
    isMetered() {
        return View.isFreeArticleAvailable() <= 0;
    }
    // Mirrors Connext v2 MeterLevels enum: Free=1, Metered=2, Premium=3
    getMeterLevelId(level) {
        switch (level === null || level === void 0 ? void 0 : level.toLowerCase()) {
            case 'free': return 1;
            case 'metered': return 2;
            case 'premium': return 3;
            default: return 2;
        }
    }
}
export const ViewsService = new ViewsServiceClass();
